import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SignaturePad } from "@/components/SignaturePad";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileSignature,
  Plus,
  Copy,
  Download,
  Trash2,
  PenLine,
  Loader2,
  CheckCircle2,
  Clock,
} from "lucide-react";

const statusMeta = {
  open: { label: "Awaiting signatures", className: "bg-muted text-muted-foreground" },
  partial: { label: "Partially signed", className: "bg-amber-100 text-amber-700" },
  completed: { label: "Completed", className: "bg-emerald-100 text-emerald-700" },
};

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "Cleaning Services Agreement", client_name: "", client_email: "", property_id: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [signTarget, setSignTarget] = useState(null);
  const [ownerSig, setOwnerSig] = useState({ name: "", mode: "type", image: "" });

  const load = () => api.get("/contracts").then((r) => setContracts(r.data));
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data));
  }, []);

  const openNew = () => {
    setForm({ title: "Cleaning Services Agreement", client_name: "", client_email: "", property_id: "", body: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.client_name.trim()) return toast.error("Enter the client's name");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.body) delete payload.body; // use server default
      if (!payload.property_id) delete payload.property_id;
      await api.post("/contracts", payload);
      toast.success("Contract created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create contract");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (c) => {
    const link = `${window.location.origin}/contract/${c.public_token}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success("Client signing link copied!"),
      () => toast.error("Could not copy link")
    );
  };

  const downloadPdf = async (c) => {
    setBusy(`pdf-${c.id}`);
    try {
      const res = await api.get(`/contracts/${c.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract-${c.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF");
    } finally {
      setBusy("");
    }
  };

  const submitOwnerSign = async () => {
    if (!ownerSig.name.trim()) return toast.error("Type your name to sign");
    setBusy("sign");
    try {
      await api.post(`/contracts/${signTarget.id}/sign-owner`, ownerSig);
      toast.success("You've signed the contract");
      setSignTarget(null);
      setOwnerSig({ name: "", mode: "type", image: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign");
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("Contract deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Could not delete contract");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">Agreements</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Contracts</h1>
        </div>
        <Button onClick={openNew} data-testid="new-contract-button">
          <Plus className="h-4 w-4 mr-2" /> New contract
        </Button>
      </div>

      {contracts.length === 0 ? (
        <Card className="p-12 text-center" data-testid="contracts-empty">
          <FileSignature className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">
            No contracts yet. Create one, sign it, and send the link to your client for signature.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => {
            const meta = statusMeta[c.status] || statusMeta.open;
            return (
              <Card key={c.id} className="p-5" data-testid={`contract-row-${c.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold">{c.title}</span>
                      <Badge className={`${meta.className} border-0`}>{meta.label}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 truncate">
                      {c.client_name}
                      {c.client_email ? ` (${c.client_email})` : ""}
                      {c.property_name ? ` · ${c.property_name}` : ""}
                    </p>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className={`inline-flex items-center gap-1 ${c.owner_signature ? "text-emerald-600" : "text-muted-foreground"}`}>
                        {c.owner_signature ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                        You {c.owner_signature ? "signed" : "not signed"}
                      </span>
                      <span className={`inline-flex items-center gap-1 ${c.client_signature ? "text-emerald-600" : "text-muted-foreground"}`}>
                        {c.client_signature ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                        Client {c.client_signature ? "signed" : "not signed"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {!c.owner_signature && (
                      <Button
                        size="sm"
                        onClick={() => { setSignTarget(c); setOwnerSig({ name: "", mode: "type", image: "" }); }}
                        data-testid={`sign-contract-${c.id}`}
                      >
                        <PenLine className="h-4 w-4 mr-1" /> Sign
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => copyLink(c)} data-testid={`copy-link-${c.id}`}>
                      <Copy className="h-4 w-4 mr-1" /> Client link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPdf(c)}
                      disabled={busy === `pdf-${c.id}`}
                      data-testid={`download-contract-${c.id}`}
                    >
                      {busy === `pdf-${c.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteTarget(c)} data-testid={`delete-contract-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* New contract dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display">New contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="contract-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Client name *</Label>
                <Input
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  data-testid="contract-client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Client email</Label>
                <Input
                  type="email"
                  value={form.client_email}
                  onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                  data-testid="contract-client-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Property (optional)</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="contract-property-select">
                  <SelectValue placeholder="Link a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contract terms</Label>
              <Textarea
                rows={10}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Leave blank to use our standard cleaning services agreement, or write your own terms."
                data-testid="contract-body-input"
              />
              <p className="text-[11px] text-muted-foreground">Fully editable — leave blank for our standard template.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-contract-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner sign dialog */}
      <Dialog open={!!signTarget} onOpenChange={(o) => !o && setSignTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Sign contract</DialogTitle>
          </DialogHeader>
          <SignaturePad onChange={setOwnerSig} testidPrefix="owner-sig" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignTarget(null)}>Cancel</Button>
            <Button onClick={submitOwnerSign} disabled={busy === "sign"} data-testid="submit-owner-sign">
              {busy === "sign" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign &amp; save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contract?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" for {deleteTarget?.client_name} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-contract">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-contract"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
